import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Send, Sparkles, ChevronLeft, Bot, User as UserIcon } from "lucide-react";

const STARTERS = [
  "Shaadi ke liye pehli mulaqat me kya baat karein?",
  "Family ko modern shaadi ke liye kaise convince karun?",
  "Horoscope match zaroori hai ya nahi?",
  "Long-distance rishta accept karna chahiye?",
];

export default function AIAssistant() {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionShort] = useState(() => "s" + Date.now().toString(36));
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get(`/ai/history/${sessionShort}`).then(({ data }) => {
      const list = [];
      (data.messages || []).forEach((m) => {
        list.push({ role: "user", text: m.user_text });
        list.push({ role: "bot", text: m.assistant_text });
      });
      setMsgs(list);
    }).catch(() => {});
  }, [sessionShort]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs]);

  const send = async (prefillText) => {
    const t = (prefillText ?? text).trim();
    if (!t || sending) return;
    setMsgs((m) => [...m, { role: "user", text: t }]);
    setText("");
    setSending(true);
    try {
      const { data } = await api.post("/ai/chat", { session_id: sessionShort, text: t });
      setMsgs((m) => [...m, { role: "bot", text: data.reply }]);
    } catch (e) {
      toast.error(formatError(e));
      setMsgs((m) => [...m, { role: "bot", text: "Sorry, main abhi jawaab nahi de paa raha. Thodi der baad try karein." }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6" data-testid="ai-assistant-page">
      <div className="flex items-center justify-between mb-4">
        <Link to="/home" className="text-xs uppercase tracking-widest text-[#57534E] hover:text-[#C85A53]">
          <ChevronLeft className="w-3.5 h-3.5 inline mr-1" /> Home
        </Link>
        <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white px-2 py-0.5 rounded-full">
          <Sparkles className="w-3 h-3" /> Powered by Umaang AI
        </div>
      </div>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-11 h-11 rounded-xl grid place-items-center bg-gradient-to-br from-[#EDE9FE] to-[#FCE7F3] text-[#7C3AED]">
          <Bot className="w-5 h-5" />
        </div>
        <div>
          <h1 className="font-display text-3xl">Umaang Sahayak</h1>
          <p className="text-xs text-[color:var(--umg-text-2)]">Aapka private matrimony advisor · 24/7 Hindi & English support</p>
        </div>
      </div>

      <div ref={scrollRef} className="border rounded-2xl h-[60vh] overflow-y-auto p-3 sm:p-4 space-y-3" style={{ borderColor: "var(--umg-border)", background: "var(--umg-surface)" }}>
        {msgs.length === 0 && (
          <div className="text-center text-sm text-[color:var(--umg-text-2)] py-8">
            <p className="mb-4">👋 Namaste! Aap kuch bhi puchein — matrimony, family, values, ya rishton ke baare me.</p>
            <div className="grid sm:grid-cols-2 gap-2 max-w-xl mx-auto">
              {STARTERS.map((s) => (
                <button
                  key={s}
                  data-testid={`ai-starter-${s.slice(0,10)}`}
                  onClick={() => send(s)}
                  className="text-left text-xs p-3 rounded-xl border hover:shadow-sm hover:bg-[#F5F3FF] transition"
                  style={{ borderColor: "#DDD6FE", background: "#F9FAFB" }}
                >{s}</button>
              ))}
            </div>
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={`msg-${i}-${m.role}`} data-testid={`ai-msg-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} gap-2`}>
            {m.role === "bot" && (
              <div className="w-7 h-7 rounded-full grid place-items-center bg-gradient-to-br from-[#7C3AED] to-[#DB2777] text-white shrink-0 mt-1">
                <Bot className="w-3.5 h-3.5" />
              </div>
            )}
            <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${m.role === "user"
              ? "bg-gradient-to-r from-[#C85A53] to-[#DB2777] text-white rounded-br-md"
              : "bg-[#F5F3FF] text-[#1F2937] rounded-bl-md"}`}>
              {m.text}
            </div>
            {m.role === "user" && (
              <div className="w-7 h-7 rounded-full grid place-items-center bg-[#FEE2E2] text-[#B91C1C] shrink-0 mt-1">
                <UserIcon className="w-3.5 h-3.5" />
              </div>
            )}
          </div>
        ))}
        {sending && (
          <div className="flex justify-start gap-2">
            <div className="w-7 h-7 rounded-full grid place-items-center bg-gradient-to-br from-[#7C3AED] to-[#DB2777] text-white shrink-0 mt-1">
              <Bot className="w-3.5 h-3.5" />
            </div>
            <div className="px-3 py-2 rounded-2xl bg-[#F5F3FF] text-[#7C3AED] text-sm inline-flex items-center gap-1">
              <span className="animate-bounce">·</span><span className="animate-bounce" style={{ animationDelay: "0.15s" }}>·</span><span className="animate-bounce" style={{ animationDelay: "0.3s" }}>·</span>
            </div>
          </div>
        )}
      </div>

      <div className="mt-3 flex items-center gap-2">
        <Input
          data-testid="ai-input"
          placeholder="Kuch bhi puchein — Hindi, English, ya Hinglish me…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); send(); } }}
          className="flex-1"
          disabled={sending}
        />
        <Button data-testid="ai-send" onClick={() => send()} disabled={sending || !text.trim()} className="btn-pill bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white h-10 px-4">
          <Send className="w-4 h-4" />
        </Button>
      </div>
      <p className="text-[10px] text-[color:var(--umg-text-2)] mt-2 text-center">
        Ye AI advisor sirf general guidance ke liye hai. Legal / medical / financial advice ke liye specialist se milein.
      </p>
    </div>
  );
}
